/**
 * Zeiterfassung – Sammelseite mit rollenbasierten Tabs:
 *   1. Stempeln    – persoenliche Stempeluhr + Tagesliste (alle)
 *   2. Uebersicht  – Mitarbeiter-Uebersicht + Heatmap (time_tracking:view)
 *   3. Korrekturen – Korrekturantraege (alle: eigene + Genehmigung wenn Recht)
 *
 * Der aktive Tab wird in der URL als ?tab=... persistiert. Alte Routen
 * /zeiterfassung/uebersicht und /zeiterfassung/korrekturen leiten weiter.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Clock, ClipboardCheck, FileEdit } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import TimeTracking from './TimeTracking';
import TimeTrackingOverview from './TimeTrackingOverview';
import TimeCorrections from './TimeCorrections';

const TAB_KEYS = {
  STEMPELN: 'stempeln',
  UEBERSICHT: 'uebersicht',
  KORREKTUREN: 'korrekturen',
};

export default function ZeiterfassungHub() {
  const { hasPermission } = useAuth();
  const canViewAll = hasPermission('time_tracking', 'view');

  const [searchParams, setSearchParams] = useSearchParams();
  const [pendingCorrections, setPendingCorrections] = useState(0);

  const availableTabs = useMemo(() => {
    const tabs = [
      { key: TAB_KEYS.STEMPELN, label: 'Stempeln', icon: Clock, testid: 'tab-stempeln' },
    ];
    if (canViewAll) {
      tabs.push({ key: TAB_KEYS.UEBERSICHT, label: 'Übersicht', icon: ClipboardCheck, testid: 'tab-uebersicht' });
    }
    // Korrekturen-Tab sehen alle (Mitarbeiter sehen ihre eigenen, Manager alle)
    tabs.push({ key: TAB_KEYS.KORREKTUREN, label: 'Korrekturen', icon: FileEdit, testid: 'tab-korrekturen' });
    return tabs;
  }, [canViewAll]);

  const activeTab = useMemo(() => {
    const requested = searchParams.get('tab');
    if (requested && availableTabs.some((t) => t.key === requested)) return requested;
    // Default: Manager mit pending-Korrekturen → direkt zur Genehmigungs-Queue
    if (canViewAll && pendingCorrections > 0) return TAB_KEYS.KORREKTUREN;
    return TAB_KEYS.STEMPELN;
  }, [searchParams, availableTabs, canViewAll, pendingCorrections]);

  const handleTabChange = (val) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', val);
    setSearchParams(next, { replace: true });
  };

  // Pending-Korrekturen zaehlen fuer den Badge
  useEffect(() => {
    if (!canViewAll) return;
    let cancelled = false;
    api.get('/time-tracking/corrections', { params: { status: 'pending' } })
      .then((res) => { if (!cancelled) setPendingCorrections((res.data || []).length); })
      .catch(() => { if (!cancelled) setPendingCorrections(0); });
    return () => { cancelled = true; };
  }, [canViewAll]);

  return (
    <DashboardLayout>
      <div className="space-y-4 animate-fade-in" data-testid="zeiterfassung-hub-page">
        <div>
          <h1 className="font-heading font-bold text-xl sm:text-3xl text-foreground">Zeiterfassung</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Stempeluhr, Übersicht und Korrekturen — alles auf einer Seite.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-flow-col auto-cols-fr">
            {availableTabs.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={t.testid} className="flex items-center gap-2">
                <t.icon className="h-4 w-4" />
                <span>{t.label}</span>
                {t.key === TAB_KEYS.KORREKTUREN && pendingCorrections > 0 && canViewAll && (
                  <Badge variant="destructive" className="ml-1 px-1.5 py-0 text-[10px]" data-testid="pending-corrections-badge">
                    {pendingCorrections}
                  </Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={TAB_KEYS.STEMPELN} className="mt-4">
            <TimeTracking embedded />
          </TabsContent>

          {canViewAll && (
            <TabsContent value={TAB_KEYS.UEBERSICHT} className="mt-4">
              <TimeTrackingOverview embedded />
            </TabsContent>
          )}

          <TabsContent value={TAB_KEYS.KORREKTUREN} className="mt-4">
            <TimeCorrections embedded />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
